import { useState } from "react";
import { useParams } from "react-router-dom";
import * as Team from "src/state/modules/team";
import Styles from "./PlayerProfilePage.module.scss";
import { OldGrid } from "src/components/atoms/OldGrid/OldGrid";
import cn from "classnames";
import { OldRow } from "src/components/atoms/OldGrid/OldRow";
import { TabViewChooser } from "src/components/compounds/TabViewChooser/TabViewChooser";
import { SprayChart } from "src/components/compounds/SprayChart";
import { PlayerProfileHeader } from "./PlayerProfileHeader";
import StatsTable from "../../../compounds/StatsTable/StatsTable";
import { mergeForPlayerStatsGrid, PlayerStatsProps } from "../stats-columns/common-columns";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { Button } from "src/components/atoms/Button/Button";
import { exportPlayerStatsCsv } from "src/utils/export-player-stats";
import {
  useGetTeamByIDQuery,
  useGetTeamEventAndSummariesByIDQuery,
  useGetTeamPlayerByIDQuery,
  useGetTeamPlayerStatsByIDQuery,
  useGetTeamSprayChartByIDQuery,
} from "src/state/modules/api";

// Rewrites each game's AVG/OBP/OPS/SLG to the season-to-date running value (matching GameChanger's
// player game log) by accumulating the counting stats game-by-game in chronological order. Counting
// stats and every other field stay per-game. Returns new objects — the source stats come from RTK
// Query's frozen cache and must not be mutated. Formulas mirror GameChanger:
//   AVG = H/AB   SLG = TB/AB   OBP = (H+BB+HBP)/(AB+BB+HBP+SF)   OPS = OBP+SLG
function withCumulativeBattingRates(games: PlayerStatsProps[]): PlayerStatsProps[] {
  const chronological = [...games].sort(
    (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
  );
  let ab = 0;
  let h = 0;
  let bb = 0;
  let hbp = 0;
  let sf = 0;
  let tb = 0;
  return chronological.map((game) => {
    // GameChanger omits the offense blob for games where the player never batted
    // (pitcher-only appearances, courtesy runners, defensive subs) — leave those rows as-is.
    const o = game.stats?.offense;
    if (!o) return game;
    ab += o.ab ?? 0;
    h += o.h ?? 0;
    bb += o.bb ?? 0;
    hbp += o.hbp ?? 0;
    sf += o.shf ?? 0;
    tb += o.tb ?? 0;
    const obpDen = ab + bb + hbp + sf;
    const avg = ab > 0 ? h / ab : 0;
    const slg = ab > 0 ? tb / ab : 0;
    const obp = obpDen > 0 ? (h + bb + hbp) / obpDen : 0;
    return {
      ...game,
      stats: { ...game.stats, offense: { ...o, avg, obp, slg, ops: obp + slg } },
    };
  });
}

export default function PlayerProfilePage() {
  const { id, pid } = useParams();
  const teamID = id ?? "";
  const playerID = pid ?? "";
  const {
    data: schedule,
    isLoading: scheduleIsLoading,
  } = useGetTeamEventAndSummariesByIDQuery(id ?? "");
  const {
    data: player,
    isLoading: playerIsLoading,
  } = useGetTeamPlayerByIDQuery({ id: teamID, pid: playerID });
  const { data: sprayCharts,
    isLoading: sprayIsLoading } = useGetTeamSprayChartByIDQuery(teamID);
  const { data: team } = useGetTeamByIDQuery(teamID);
  // Season-aggregated totals (batting/pitching/fielding) for this player; powers the CSV export's
  // "Season Total" rows alongside the per-game breakdown derived from the spray charts.
  const { data: seasonStats } = useGetTeamPlayerStatsByIDQuery({ id: teamID, pid: playerID });

  const views = ["Stats", "Spray Charts"];
  const [currentView, setCurrentView] = useState(views[0]);

  if (scheduleIsLoading || playerIsLoading || sprayIsLoading) return <PageLoader1 />;
  if (schedule === undefined || player === undefined || sprayCharts === undefined) return <PageLoader1 />;

  let playerStats = [] as PlayerStatsProps[];
  sprayCharts.forEach((value) => {
    if (value.player_stats.players[player.id] === undefined) return;
    let curEvt = schedule.find((evt) => {
      return evt.id === value.event_id;
    });
    playerStats.push({
      opponent: curEvt?.opponentTeam?.name ?? "UNKNOWN",
      date: curEvt?.start ?? "",
      eventID: value.event_id,
      stats: value.player_stats.players[player.id]?.stats,
    });
  });

  // The spray-chart payload carries each game's *own* AVG/OBP/OPS/SLG (the rate for that single
  // game). GameChanger's player game log instead shows those four as running season-to-date values
  // while keeping every counting stat per-game, so recompute the rates here by accumulating the
  // counting stats in chronological order. Everything else on each row is left untouched.
  playerStats = withCumulativeBattingRates(playerStats);

  return (
    <OldGrid centered={true} className={cn(Styles.PlayerProfileContainer)}>
      <OldRow padded={false} className={Styles.HeaderSection}>
        <PlayerProfileHeader sport={Team.Sport.BASEBALL} player={player} team={team}></PlayerProfileHeader>
      </OldRow>
      <OldRow padded={false} className={Styles.ControlsRow}>
        <TabViewChooser
          variant="selector"
          options={views}
          active={currentView}
          onClick={(view) => setCurrentView(view)}
        />
        <Button
          title="Export CSV"
          type="stroke"
          size="small"
          onClick={() =>
            exportPlayerStatsCsv({
              player,
              team,
              seasonStats: seasonStats?.stats,
              games: playerStats,
            })
          }
        />
      </OldRow>
      {currentView === views[0] ? <StatsTable grid={mergeForPlayerStatsGrid(player, playerStats)} eventMode /> : <SprayChart />}
    </OldGrid>
  );
}
